import { AsyncPipe, DecimalPipe } from '@angular/common'
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatButtonModule, MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { map, Observable, of, Subscription, tap } from 'rxjs'
import type { CitationMinuteNodes } from '../../../../../backend/src/citations/minutes-citations-api.dto'
import { Call, CallsService, canDownloadMinutes, hasEditAccess, MetricType } from '../../calls.service'
import { InfoDialogComponent } from '../../info-dialog/info-dialog.component'
import { MetricsService } from '../../metrics.service'
import { ToastService } from '../../toast.service'
import { EditorSettingsService, EDITOR_ZOOM_MAX, EDITOR_ZOOM_MIN } from '../editor-settings.service'
import { LegacyMinutesNoticeComponent } from '../legacy-minutes-notice/legacy-minutes-notice.component'
import { OutlineComponent } from '../outline/outline.component'
import { ActionItemsComponent } from '../action-items/action-items.component'
import { ActionItemsOverview, ActionItemsService } from '../action-items/action-items.service'
import { hasActionItemsTab } from '../action-items/action-items-view'
import { OpenDocumentEvent, OpenMeetingEvent } from '../tiptap-editor/extensions/citation-plugin'
import { TiptapEditorComponent } from '../tiptap-editor/tiptap-editor.component'
import { RevisionSummary } from '../tiptap-editor/editor-revisions.service'
import { RestrictedMinutesComponent } from '../tiptap-editor/restricted-minutes/restricted-minutes.component'
import { ProgressLoaderComponent } from '../../common/progress-loader/progress-loader.component'
import { CallEntitiesOverview, CallEntitiesService, CallEntity } from '../call-entities/call-entities.service'
import { CallEntityReadiness, resolveCallEntityReadiness } from '../call-entities/call-entity-readiness'
import {
  ManageCallEntitiesDialogComponent,
  ManageCallEntitiesDialogData,
} from '../call-entities/manage-call-entities-dialog/manage-call-entities-dialog.component'
import { MinutesDocumentSwitcherComponent } from '../call-entities/minutes-document-switcher/minutes-document-switcher.component'

@Component({
  selector: 'app-call-outputs-container',
  standalone: true,
  imports: [
    LegacyMinutesNoticeComponent,
    OutlineComponent,
    ActionItemsComponent,
    TiptapEditorComponent,
    RestrictedMinutesComponent,
    MatIcon,
    MatIconButton,
    TranslatePipe,
    TranslateDirective,
    MatTooltip,
    MatMenuModule,
    MatButtonModule,
    AsyncPipe,
    DecimalPipe,
    MatIconButtonSizesModule,
    MinutesDocumentSwitcherComponent,
    ProgressLoaderComponent,
  ],
  templateUrl: './call-outputs-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './call-outputs-container.component.scss',
})
export class CallOutputsContainerComponent implements OnChanges, OnDestroy {
  @ViewChild(TiptapEditorComponent)
  tiptapEditor: TiptapEditorComponent | null = null

  @ViewChild(OutlineComponent)
  outlineComponent: OutlineComponent | undefined

  @ViewChild(RestrictedMinutesComponent)
  restrictedMinutes: RestrictedMinutesComponent | null = null

  /** Whichever minutes view is showing: the editor, or a Restricted user's sections. Both search their own content. */
  get findReplaceHost(): { findReplaceOpen$: Observable<boolean>; toggleFindReplace(): void } | null {
    return this.tiptapEditor ?? this.restrictedMinutes
  }

  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canUserEdit!: boolean

  @Input({ required: true })
  canUserSuggest!: boolean

  @Input({ required: true })
  canUserComment!: boolean

  @Input({ required: true })
  canResolveAnyThread!: boolean

  @Input()
  isSensitiveModeEnabled: boolean = false

  @Output()
  openAgendaUpload = new EventEmitter<void>()

  @Output()
  openMeeting = new EventEmitter<OpenMeetingEvent>()

  @Output()
  openDocument = new EventEmitter<OpenDocumentEvent>()

  get showExpandAll(): boolean {
    return this.outlineComponent?.showExpandAll() ?? false
  }

  currentTab: 'EDITOR' | 'OUTLINE' | 'ACTIONS' = 'EDITOR'

  get showCitations(): boolean {
    return this.editorSettingsService.showCitations
  }

  set showCitations(value: boolean) {
    this.editorSettingsService.setShowCitations(value)
  }

  get hasCitations(): boolean {
    if (this.isRestricted) {
      return false
    }
    return this.selectedCallEntityId === null ? this.call.minutesCitations !== null : this.latestEntityCitations !== null
  }

  /**
   * A Restricted user sees only their own items, so the whole-minutes tools (revisions, word count, export) are hidden,
   * and nothing behind the minutes either — no citations, which point into the recording and the papers.
   */
  get isRestricted(): boolean {
    return this.call.accessLevel === 'RESTRICTED'
  }

  /** Action items are their own records, so they stay editable while the minutes are still being generated. */
  get canUserEditActionItems(): boolean {
    return hasEditAccess(this.call)
  }

  get canExportCurrentTab(): boolean {
    if (this.isRestricted || this.currentTab === 'ACTIONS') {
      return false
    }
    if (this.currentTab === 'OUTLINE') {
      return this.call.allowOutlineDownload
    }
    const selectedEntity = this.selectedEntity
    if (selectedEntity !== null) {
      return canDownloadMinutes(this.call) && selectedEntity.hasMinutes
    }
    if (!this.call.hasEditorDocument) {
      return false
    }
    // With no minutes the editor content is seeded from the outline, so exporting it
    // requires outline download permission (mirrors the backend download guard).
    return canDownloadMinutes(this.call) && (this.call.hasMinutes || this.call.allowOutlineDownload)
  }

  toggleCitations() {
    this.editorSettingsService.setShowCitations(!this.showCitations)
  }

  get showWordCount(): boolean {
    return this.editorSettingsService.showWordCount
  }

  toggleWordCount() {
    this.editorSettingsService.setShowWordCount(!this.showWordCount)
  }

  toggleEditorMaximized() {
    this.editorSettingsService.setIsEditorMaximized(!this.editorSettingsService.isEditorMaximized)
  }

  readonly editorZoomMin = EDITOR_ZOOM_MIN
  readonly editorZoomMax = EDITOR_ZOOM_MAX

  zoomIn() {
    this.editorSettingsService.zoomIn()
  }

  zoomOut() {
    this.editorSettingsService.zoomOut()
  }

  resetZoom() {
    this.editorSettingsService.resetZoom()
  }

  relayOpenDocument(event: OpenDocumentEvent) {
    this.editorSettingsService.setIsEditorMaximized(false)
    this.openDocument.emit(event)
  }

  relayOpenMeeting(event: OpenMeetingEvent) {
    this.editorSettingsService.setIsEditorMaximized(false)
    this.openMeeting.emit(event)
  }

  toastService = inject(ToastService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  metricsService = inject(MetricsService)
  callsService = inject(CallsService)
  editorSettingsService = inject(EditorSettingsService)
  callEntitiesService = inject(CallEntitiesService)
  actionItemsService = inject(ActionItemsService)
  showActionsTab = false
  private actionItemsSubscription: Subscription | null = null
  private latestActionItems: ActionItemsOverview | null = null
  isEditorMaximized$ = this.editorSettingsService.isEditorMaximized$
  zoomLevel$ = this.editorSettingsService.zoomLevel$

  entitiesOverview$: Observable<CallEntitiesOverview | null> = of(null)
  entityCitations$: Observable<CitationMinuteNodes | null> = of(null)
  selectedCallEntityId: number | null = null
  private latestOverview: CallEntitiesOverview | null = null
  private latestEntityCitations: CitationMinuteNodes | null = null
  private generateEntityMinutesMutation = this.callEntitiesService.generateEntityMinutesMutation()

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['call']
    if (change === undefined) {
      return
    }
    const previousCall = change.firstChange ? null : (change.previousValue as Call)
    if (previousCall === null || previousCall.id !== this.call.id || previousCall.allowActionItems !== this.call.allowActionItems) {
      this.watchActionItems()
    } else {
      this.followActionItemDetection()
    }
    if (previousCall !== null && previousCall.id === this.call.id && previousCall.allowCallEntities === this.call.allowCallEntities) {
      return
    }
    this.latestOverview = null
    this.selectDocument(null)
    this.entitiesOverview$ = this.call.allowCallEntities
      ? this.callEntitiesService.getOverview(this.call.id).pipe(
          map((result) => result.data ?? null),
          tap((overview) => this.keepSelectionWithin(overview))
        )
      : of(null)
  }

  get selectedEntity(): CallEntity | null {
    return this.latestOverview?.entities.find((entity) => entity.id === this.selectedCallEntityId) ?? null
  }

  selectedEntityReadiness(overview: CallEntitiesOverview, entity: CallEntity): CallEntityReadiness {
    return resolveCallEntityReadiness(overview, entity)
  }

  selectDocument(callEntityId: number | null) {
    this.selectedCallEntityId = callEntityId
    this.latestEntityCitations = null
    this.entityCitations$ =
      callEntityId === null
        ? of(null)
        : this.callEntitiesService.getCitations(this.call.id, callEntityId).pipe(
            map((result) => result.data?.citations ?? null),
            tap((citations) => {
              this.latestEntityCitations = citations
            })
          )
  }

  openManageEntities() {
    this.dialog.open<ManageCallEntitiesDialogComponent, ManageCallEntitiesDialogData>(ManageCallEntitiesDialogComponent, {
      data: { callId: this.call.id },
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
    })
  }

  async generateSelectedEntityMinutes() {
    if (this.selectedCallEntityId === null) {
      return
    }
    await this.generateEntityMinutesMutation.mutateAsync({ callId: this.call.id, callEntityId: this.selectedCallEntityId })
  }

  /** An entity deleted elsewhere takes the reader back to the whole meeting rather than to a document that is gone. */
  private keepSelectionWithin(overview: CallEntitiesOverview | null) {
    this.latestOverview = overview
    const isSelectionGone = this.selectedCallEntityId !== null && (overview?.entities.every((entity) => entity.id !== this.selectedCallEntityId) ?? true)
    if (isSelectionGone) {
      this.selectDocument(null)
    }
  }

  ngOnDestroy() {
    this.actionItemsSubscription?.unsubscribe()
  }

  /**
   * The Actions tab is for organizations with the setting on. It follows action item detection rather than the outline:
   * it shows from the end of the meeting, while the processing that leads to detection runs, and stays once detection
   * has run or items exist.
   */
  private watchActionItems() {
    this.actionItemsSubscription?.unsubscribe()
    this.showActionsTab = false
    this.latestActionItems = null
    if (!this.call.allowActionItems) {
      if (this.currentTab === 'ACTIONS') {
        this.currentTab = 'EDITOR'
      }
      return
    }
    this.actionItemsSubscription = this.actionItemsService.list(this.call.id).subscribe((result) => {
      this.latestActionItems = result.data ?? null
      this.updateActionsTabVisibility()
    })
  }

  /** The list only polls while detection runs, so until it starts, each update of the call re-fetches it. */
  private followActionItemDetection() {
    if (this.latestActionItems === null) {
      return
    }
    if (this.latestActionItems.detectionStatus === 'NOT_STARTED') {
      this.actionItemsService.refresh(this.call.id)
    }
    this.updateActionsTabVisibility()
  }

  private updateActionsTabVisibility() {
    this.showActionsTab = this.latestActionItems !== null && hasActionItemsTab(this.latestActionItems, this.call)
    if (!this.showActionsTab && this.currentTab === 'ACTIONS') {
      this.currentTab = 'EDITOR'
    }
  }

  switchTab(tab: 'EDITOR' | 'OUTLINE' | 'ACTIONS') {
    this.currentTab = tab
    if (tab === 'OUTLINE') {
      this.metricsService.sendMetric(this.call.id, MetricType.CLICK_ON_OUTLINE_TAB)
    }
  }

  async exportDocument(format: 'pdf' | 'docx', revision: RevisionSummary | null = null) {
    const contentType = this.currentTab === 'EDITOR' ? 'minutes' : 'outline'
    const infoDialogRef = this.dialog.open(InfoDialogComponent, {
      width: '400px',
      data: {
        title: this.translateService.instant(contentType === 'minutes' ? 'CALLS.MINUTES_EXPORT_DIALOG_TITLE' : 'CALLS.OUTLINE_EXPORT_DIALOG_TITLE'),
        displayLoadingSpinner: true,
        displayConfirmButton: false,
      },
    })
    const selectedEntity = this.selectedEntity
    const export$ =
      contentType === 'minutes'
        ? this.callsService.exportMinutes(this.call, format, selectedEntity === null ? null : { id: selectedEntity.id, name: selectedEntity.name }, revision)
        : this.callsService.exportOutline(this.call, format)
    const subscription = export$.subscribe({
      error: () => {
        this.toastService.show('CALLS.EXPORT_FAILED')
        infoDialogRef.close()
      },
      complete: () => {
        infoDialogRef.close()
      },
    })
    infoDialogRef.afterClosed().subscribe(() => {
      subscription.unsubscribe()
    })
  }

  expandAll() {
    this.outlineComponent?.expandAll()
  }

  collapseAll() {
    this.outlineComponent?.collapseAll()
  }
}
